import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ArrowRight, Tag, X, Check } from 'lucide-react';
import { allBlogPosts } from '../data/blogData';
import UnifiedHeader from '../components/UnifiedHeader';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

// Blog Post Modal - Shows full article with author bio
const BlogPostModal = ({ post, onClose, onTryNow }) => {
  if (!post) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 z-[100] overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start justify-center px-4 pt-20 pb-8">
        <div 
          className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="relative">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-72 object-cover rounded-t-2xl"
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-md z-10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-8">
          <div className="flex items-center space-x-3 mb-4">
            <span className="px-3 py-1 bg-rose-100 text-rose-600 text-sm font-medium rounded-full">
              {post.category}
            </span>
            {post.tags && post.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                #{tag}
              </span>
            ))}
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">{post.title}</h2>
          
          {/* Author Info */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {post.author?.charAt(0) || 'V'}
            </div>
            <div>
              <p className="font-semibold text-gray-800">{post.author}</p>
              <p className="text-sm text-gray-500">{post.authorBio || 'VirtualFit Team'}</p>
            </div>
          </div>
          
          <div className="flex items-center text-sm text-gray-500 mb-6">
            <Calendar className="w-4 h-4 mr-1" />
            <span>{post.date}</span>
            <span className="mx-3">•</span>
            <Clock className="w-4 h-4 mr-1" />
            <span>{post.readTime}</span>
          </div>
          
          {/* Article Content */}
          <div className="prose prose-lg max-w-none text-gray-700">
            {post.content ? (
              <div className="whitespace-pre-line leading-relaxed">
                {post.content.split('\n').map((paragraph, idx) => {
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={idx} className="text-2xl font-bold text-gray-800 mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
                  }
                  if (paragraph.startsWith('### ')) {
                    return <h3 key={idx} className="text-xl font-semibold text-gray-800 mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
                  }
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return <p key={idx} className="font-semibold text-gray-800 mt-4">{paragraph.replace(/\*\*/g, '')}</p>;
                  }
                  if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
                    return <li key={idx} className="ml-4">{paragraph.replace(/^[-*] /, '')}</li>;
                  }
                  if (paragraph.trim() === '') {
                    return <br key={idx} />;
                  }
                  return <p key={idx} className="mb-4">{paragraph}</p>;
                })}
              </div>
            ) : (
              <p className="text-gray-600">{post.excerpt}</p>
            )}
          </div>
          
          <div className="mt-8 pt-6 border-t flex items-center justify-between">
            <button
              onClick={onTryNow}
              className="px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full font-semibold hover:shadow-lg transition-all"
            >
              Try VirtualFit Now
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-semibold hover:bg-gray-200 transition-all"
            >
              Close Article
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

const BlogPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visiblePosts, setVisiblePosts] = useState(9);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // Use imported blog data
  const blogPosts = allBlogPosts;

  const categories = ["All", "Tips & Tricks", "Fashion Trends", "Guides", "Industry Insights", "Sustainability", "Style Guides"];

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const displayedPosts = filteredPosts.slice(0, visiblePosts);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      try {
        const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        
        if (response.ok) {
          setSubscribed(true);
          setEmail('');
          setTimeout(() => setSubscribed(false), 5000);
        } else {
          const data = await response.json();
          alert(data.detail || 'Failed to subscribe. Please try again.');
        }
      } catch (err) {
        console.error('Newsletter subscription error:', err);
        // Still show success for now even if backend fails
        setSubscribed(true);
        setEmail('');
        setTimeout(() => setSubscribed(false), 5000);
      }
    }
  };

  const handleLoadMore = () => {
    setVisiblePosts(prev => prev + 3);
  };

  const handleTryNow = () => {
    setSelectedPost(null);
    navigate(isAuthenticated ? '/dashboard' : '/subscribe');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
      {/* Blog Post Modal */}
      <BlogPostModal 
        post={selectedPost} 
        onClose={() => setSelectedPost(null)} 
        onTryNow={handleTryNow}
      />

      <UnifiedHeader />

      {/* Main Content with top padding for fixed nav */}
      <div className="pt-20 flex-1">
        <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            VirtualFit <span className="bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">Blog</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Fashion tips, style guides, and insights on how AI is transforming the way we shop for clothes.
          </p>
        </div>

        {/* Categories - Now Clickable */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category, index) => (
            <button
              key={index}
              onClick={() => { setSelectedCategory(category); setVisiblePosts(6); }}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                selectedCategory === category 
                  ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-lg' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured Post */}
        {displayedPosts.length > 0 && (
          <div className="mb-12">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto">
                  <img 
                    src={displayedPosts[0].image} 
                    alt={displayedPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-3 py-1 bg-rose-100 text-rose-600 text-sm font-medium rounded-full">
                      Featured
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-600 text-sm font-medium rounded-full">
                      {displayedPosts[0].category}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                    {displayedPosts[0].title}
                  </h2>
                  <p className="text-gray-600 mb-6">{displayedPosts[0].excerpt}</p>
                  <div className="flex items-center text-sm text-gray-500 mb-6">
                    <User className="w-4 h-4 mr-1" />
                    <span>{displayedPosts[0].author}</span>
                    <span className="mx-3">•</span>
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{displayedPosts[0].date}</span>
                    <span className="mx-3">•</span>
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{displayedPosts[0].readTime}</span>
                  </div>
                  <button 
                    onClick={() => setSelectedPost(displayedPosts[0])}
                    className="flex items-center text-rose-500 font-semibold hover:text-rose-600 transition-colors"
                  >
                    Read More <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedPosts.slice(1).map((post) => (
            <article 
              key={post.id}
              onClick={() => setSelectedPost(post)}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group cursor-pointer"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium rounded-full flex items-center">
                    <Tag className="w-3 h-3 mr-1" />
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-rose-500 transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More */}
        {visiblePosts < filteredPosts.length && (
          <div className="text-center mt-12">
            <button 
              onClick={handleLoadMore}
              className="px-8 py-3 bg-white text-gray-700 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all border border-gray-200"
            >
              Load More Articles ({filteredPosts.length - visiblePosts} remaining)
            </button>
          </div>
        )}

        {/* Newsletter */}
        <div className="mt-16 bg-gradient-to-r from-rose-500 to-orange-500 rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">Stay Updated</h3>
          <p className="mb-8 text-white/90 max-w-xl mx-auto">
            Get the latest fashion tips, style guides, and VirtualFit updates delivered to your inbox.
          </p>
          {subscribed ? (
            <div className="flex items-center justify-center space-x-2 text-white">
              <Check className="w-6 h-6" />
              <span className="text-lg font-semibold">Thanks for subscribing!</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-6 py-3 rounded-full text-gray-800 outline-none focus:ring-2 focus:ring-white/50"
              />
              <button 
                type="submit"
                className="px-8 py-3 bg-white text-rose-500 rounded-full font-semibold hover:shadow-lg transition-all"
              >
                Subscribe
              </button>
            </form>
          )}
        </div>
        </div>
      </div>

      <Footer isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default BlogPage;
